/**
 * Schema e infraestrutura do IndexedDB local-first.
 *
 * Esta fase cria o banco, as migrações e as primitivas compartilhadas. Operações completas de
 * listas, cascata e paginação entram em S1.2.
 */
import type { ScanType } from '../types/searchList';
import type { PlacePhone, PlaceReviews, PlaceSocialNetwork } from '../types/place';
import type { CaptureEngineSnapshot, StoredCaptureEngineSnapshot } from '../lib/captureEngine';
import { publishLocalListChange } from './localListChanges';
import type { SealedRecord } from '../lib/seal';

export const LOCAL_DB_NAME = 'eminer_local';
export const LOCAL_DB_VERSION = 1;

export const STORE_LISTS = 'lists';
export const STORE_LEADS = 'leads';
export const STORE_PLACES = 'places';
export const STORE_DOMAINS = 'domains';

export const INDEX_LISTS_BY_USER = 'byUser';
export const INDEX_RECORDS_BY_LIST = 'byList';

export interface LocalList {
  id: string;
  userId: string;
  module: ScanType;
  keywords?: string[];
  sites?: string[];
  domains?: string[];
  countries?: string[];
  area_code?: string[];
  locations?: string[];
  distance?: number;
  /** Contatos/places exibíveis; perfis `lookalike` sem contato não entram neste total. */
  count: number;
  searching: boolean;
  createdAt: string;
  updatedAt: string;
  lookalikeAt?: string;
  /** Maior id do snapshot remoto concluído por `lookalikeAt`. */
  lookalikeSnapshot?: string;
  enrichedAt?: string;
  backupState: 'local' | 'sending' | 'synced' | 'error';
  backupAt?: string;
  backupError?: string;
  dirtyAt?: string;
  backupAttempts?: number;
  backupNextAttemptAt?: string;
  /** Identidade determinística da remessa corrente (`listId + dirtyAt + chunkSize`). */
  backupBatchId?: string;
  backupChunkSize?: number;
  /** Índices confirmados pelo backend; permite retomar sem reenviar o que já recebeu ACK. */
  backupConfirmedChunks?: number[];
  /** Versão de `enrichedAt` já confirmada por `/enhanced`. */
  backupEnhancedAt?: string;
  /** Versão de `lookalikeAt` já confirmada por `/lookalike`. */
  backupLookalikeAt?: string;
  /** Execução, fase, schedule e cursor locais; não fazem parte da contabilidade de backup. */
  captureState?: StoredCaptureEngineSnapshot;
  /** A lista tem registros fechados (teste, decisão 293): depois da compra, é lida de novo. */
  sealedAt?: string;
}

export type LocalListContentPatch = Partial<
  Pick<
    LocalList,
    | 'count'
    | 'searching'
    | 'keywords'
    | 'sites'
    | 'domains'
    | 'countries'
    | 'area_code'
    | 'locations'
    | 'distance'
    | 'lookalikeAt'
    | 'enrichedAt'
  >
>;

export type LocalListBackupPatch = Partial<
  Pick<
    LocalList,
    | 'backupState'
    | 'backupAt'
    | 'backupError'
    | 'backupAttempts'
    | 'backupNextAttemptAt'
    | 'backupBatchId'
    | 'backupChunkSize'
    | 'backupConfirmedChunks'
  >
>;

export interface MarkListSyncedOptions {
  /** `dirtyAt` observado quando o upload começou; ausente no catch-up de marca já limpa. */
  expectedDirtyAt: string | undefined;
  backupAt: string;
  /** Marcas remotas confirmadas nesta tentativa, na mesma versão observada no início. */
  confirmedMarkers?: {
    enrichedAt?: string;
    lookalikeAt?: string;
  };
}

export interface LocalLead {
  listId: string;
  key: string;
  type: 'email' | 'phone' | 'profile';
  value: string;
  title?: string;
  url?: string;
  social_network?: string;
  username?: string;
  headline?: string;
  company?: string;
  location?: string;
  cnpj?: string;
  /** E-mail da empresa num contato de Telefones (lista da Rede do WhatsApp, decisão 457). */
  email?: string;
  mobile?: boolean;
  lookalike?: boolean;
  emailStatus?: 'valid' | 'no_mx' | 'unverified';
  phoneStatus?: 'valid' | 'invalid' | 'unverified';
  /** Acima do limite de leitura do teste: o original fica só no fechado (decisão 293). */
  deface?: boolean;
  sealed?: SealedRecord;
  createdAt: string;
}

/** Contatos que vieram do Google Maps, guardados antes da primeira gravação do enriquecimento. */
export interface LocalPlaceMapsContacts {
  phones?: PlacePhone[];
  websites?: string[];
  social_networks?: PlaceSocialNetwork[];
}

export interface LocalPlace {
  listId: string;
  key: string;
  cid: string;
  placeID?: string;
  title?: string;
  location?: number[];
  image?: string;
  images?: string[];
  photos?: number;
  posts?: number;
  address?: string[];
  address_resume?: string[];
  websites?: string[];
  social_networks?: PlaceSocialNetwork[];
  phones?: PlacePhone[];
  emails?: string[];
  categories?: string[];
  /** GCIDs das categorias (mesma ordem), para traduzi-las; ausente em registros antigos. */
  categoryIds?: string[];
  services?: unknown[];
  times?: unknown[];
  reviews?: PlaceReviews;
  price?: string;
  stars?: string;
  score?: number;
  ads?: boolean;
  claim?: string;
  cnpj?: string;
  mobile?: boolean;
  lookalike?: boolean;
  /** CIDs que o Google mostra como parecidos; só vêm com sessão do Google (decisão 178). */
  similars?: string[];
  /** Semelhante: negócios da lista que apontaram para ele ("Por que este lead?", regra 10). */
  lookalikeOf?: string[];
  /** Pontuação de semelhança (0 a 100) na rodada que o trouxe. */
  lookalikeScore?: number;
  /** 1 = parecido de um negócio da lista original. */
  lookalikeLevel?: number;
  /** De onde veio o semelhante: outra lista do usuário, a base da nuvem ou o Google. */
  lookalikeSource?: 'local' | 'base' | 'google';
  /** A rodada que o trouxe (ISO): o Desfazer tira a rodada inteira. */
  lookalikeRound?: string;
  /** Negócios da lista para os quais ele aponta de volta no Google Maps. */
  lookalikeBack?: string[];
  /**
   * Tirado da lista (decisão 199): some da tela, das contagens e das exportações, vai no backup e
   * não volta a ser sugerido. Nada é apagado de verdade.
   */
  removedAt?: string;
  enhancedAt?: string;
  enhancedCount?: {
    websites?: number;
    emails?: number;
    social_networks?: number;
    phones?: number;
    /** 1 quando a gravação trouxe um CNPJ novo (desde 24/10/2026). */
    cnpj?: number;
  };
  /**
   * O que veio do Maps antes do enriquecimento: é o que a lista sem a marca de enriquecida mostra
   * (decisão 177). Ausente em quem nunca foi enriquecido e em quem foi antes de 24/10/2026.
   */
  mapsContacts?: LocalPlaceMapsContacts;
  /** Recência da associação deste usuário no deck legado (`Place.users.updatedAt`). */
  updatedAt?: string;
  /** Acima do limite de leitura do teste: o original fica só no fechado (decisão 293). */
  deface?: boolean;
  sealed?: SealedRecord;
  createdAt: string;
}

/** Endereço imutável de um place no store, acompanhado do CID esperado. */
export interface LocalPlaceTarget {
  listId: string;
  key: string;
  cid: string;
}

export interface LocalPlaceUpdateResult {
  before: LocalPlace;
  after: LocalPlace;
}

export interface DomainVerdict {
  domain: string;
  hasMx: boolean;
  checkedAt: string;
  /** `both` representa ausência de MX confirmada pelos dois resolvedores. */
  source: 'cloudflare' | 'google' | 'both';
}

export interface LocalStorageCapacity {
  /** `undefined` quando a Storage API não existe ou não respondeu. */
  persisted?: boolean;
  usage?: number;
  quota?: number;
  available?: number;
}

export type CaptureRecordsWriteResult =
  | {
      ok: true;
      inserted: number;
      count: number;
      /** Chaves realmente novas, na ordem de inserção (alimenta "Últimos leads" da busca). */
      insertedKeys: string[];
    }
  | { ok: false; reason: 'list_not_found' | 'module_mismatch' }
  | { ok: false; reason: 'stale_run'; count: number };

export interface InsertCaptureRecordsOptions {
  /** Se informado, lista e registros só mudam enquanto o mesmo run ainda ocupar o snapshot. */
  expectedRunId?: string;
}

export interface CompleteListHydrationOptions {
  id: string;
  userId: string;
  module: ScanType;
  hydratedAt: string;
  /** O servidor devolveu registros mascarados (teste): a lista é lida de novo depois da compra. */
  sealed?: boolean;
}

export type ListHydrationWriteResult =
  | { ok: true; count: number }
  | { ok: false; reason: 'ownership_mismatch' | 'module_mismatch' };

/**
 * P3: cota cheia pausa o chamador; o banco nunca poda listas automaticamente.
 * Quem integrar a captura pode reconhecer este erro sem depender da mensagem do navegador.
 */
export class LocalDbQuotaError extends Error {
  readonly code = 'quota_exceeded';

  constructor() {
    super('O armazenamento local está cheio. Libere espaço antes de continuar a captura.');
    this.name = 'LocalDbQuotaError';
  }
}

/** Invariante da ingestão: todo registro do lote precisa pertencer à lista de destino. */
export class CaptureRecordsListMismatchError extends Error {
  readonly code = 'invalid_batch';

  constructor() {
    super('O lote contém registros destinados a outra lista.');
    this.name = 'CaptureRecordsListMismatchError';
  }
}

type RecordStore = typeof STORE_LEADS | typeof STORE_PLACES;
export type LocalRecord = LocalLead | LocalPlace;

/** Confere o tipo físico contra o módulo tipado, preservando `profile` como insumo oculto. */
export function recordMatchesListModule(record: LocalRecord, module: ScanType): boolean {
  if (module === 'places') return !('type' in record);
  if (!('type' in record)) return false;
  if (module === 'emails') return record.type === 'email' || record.type === 'profile';
  if (module === 'phones') return record.type === 'phone' || record.type === 'profile';
  return true;
}

/**
 * Perfis sem contato são matéria-prima da resolução de semelhantes, não leads entregues.
 * Eles continuam no store e no backup, mas não entram no badge, celebração ou `Search.count`.
 * Quando o módulo é conhecido, contatos incompatíveis de versões anteriores também ficam fora.
 */
export function isVisibleListRecord(record: LocalRecord, module?: ScanType): boolean {
  return (
    (module === undefined || recordMatchesListModule(record, module)) &&
    !('type' in record && record.type === 'profile') &&
    !('removedAt' in record && record.removedAt)
  );
}

let dbPromise: Promise<IDBDatabase> | null = null;
let capacityPromise: Promise<LocalStorageCapacity> | null = null;

function toError(error: unknown, fallback: string): Error {
  if (error instanceof LocalDbQuotaError) return error;
  if (
    typeof error === 'object' &&
    error != null &&
    'name' in error &&
    error.name === 'QuotaExceededError'
  ) {
    return new LocalDbQuotaError();
  }
  if (error instanceof Error) return error;
  return new Error(fallback);
}

async function inspectStorageCapacity(): Promise<LocalStorageCapacity> {
  if (typeof navigator === 'undefined' || !navigator.storage) return {};

  let persisted: boolean | undefined;
  let estimate: StorageEstimate = {};

  try {
    if (typeof navigator.storage.persist === 'function')
      persisted = await navigator.storage.persist();
  } catch {
    // A permissão pode não existir ou ser negada; isso não impede a abertura do banco.
  }

  try {
    if (typeof navigator.storage.estimate === 'function')
      estimate = await navigator.storage.estimate();
  } catch {
    // Sem estimativa ainda podemos usar IndexedDB e tratar QuotaExceededError na escrita.
  }

  const usage = estimate.usage;
  const quota = estimate.quota;
  return {
    persisted,
    usage,
    quota,
    available: usage != null && quota != null ? Math.max(0, quota - usage) : undefined,
  };
}

/** Pede armazenamento persistente e devolve a estimativa de capacidade mais recente. */
export function getLocalStorageCapacity(): Promise<LocalStorageCapacity> {
  capacityPromise ??= inspectStorageCapacity();
  return capacityPromise;
}

function createSchema(db: IDBDatabase, oldVersion: number): void {
  /**
   * Migrações são cumulativas. Nunca altere um bloco publicado; suba LOCAL_DB_VERSION e crie
   * outro `if (oldVersion < N)` para que instalações antigas e novas cheguem ao mesmo schema.
   */
  if (oldVersion < 1) {
    if (!db.objectStoreNames.contains(STORE_LISTS)) {
      const lists = db.createObjectStore(STORE_LISTS, { keyPath: 'id' });
      lists.createIndex(INDEX_LISTS_BY_USER, 'userId', { unique: false });
    }
    if (!db.objectStoreNames.contains(STORE_LEADS)) {
      const leads = db.createObjectStore(STORE_LEADS, { keyPath: ['listId', 'key'] });
      leads.createIndex(INDEX_RECORDS_BY_LIST, 'listId', { unique: false });
    }
    if (!db.objectStoreNames.contains(STORE_PLACES)) {
      const places = db.createObjectStore(STORE_PLACES, { keyPath: ['listId', 'key'] });
      places.createIndex(INDEX_RECORDS_BY_LIST, 'listId', { unique: false });
    }
    if (!db.objectStoreNames.contains(STORE_DOMAINS)) {
      db.createObjectStore(STORE_DOMAINS, { keyPath: 'domain' });
    }
  }
}

/** Abre e memoiza a conexão; a primeira abertura também executa persistência + estimativa. */
export function openLocalDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  const capacity = getLocalStorageCapacity();
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(LOCAL_DB_NAME, LOCAL_DB_VERSION);
    } catch (error) {
      reject(toError(error, 'Não foi possível abrir o banco local.'));
      return;
    }

    request.onupgradeneeded = (event) => createSchema(request.result, event.oldVersion);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(toError(request.error, 'Não foi possível abrir o banco local.'));
    request.onblocked = () =>
      reject(new Error('A atualização do banco local foi bloqueada por outra aba.'));
  });

  const memoized = Promise.all([opening, capacity]).then(([db]) => db);
  dbPromise = memoized;
  void memoized.catch(() => {
    if (dbPromise === memoized) dbPromise = null;
  });
  return dbPromise;
}

async function transaction<T>(
  stores: string | string[],
  mode: IDBTransactionMode,
  run: (tx: IDBTransaction, output: { value: T }) => void,
): Promise<T> {
  const db = await openLocalDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(stores, mode);
    const output = { value: undefined as T };
    tx.oncomplete = () => resolve(output.value);
    tx.onerror = () => reject(toError(tx.error, 'Falha na transação do banco local.'));
    tx.onabort = () => reject(toError(tx.error, 'A transação do banco local foi cancelada.'));

    try {
      run(tx, output);
    } catch (error) {
      try {
        tx.abort();
      } catch {
        // A transação pode já ter sido abortada pelo IndexedDB.
      }
      reject(toError(error, 'Falha na transação do banco local.'));
    }
  });
}

/* ------------------------------- Listas ------------------------------- */

export async function putList(list: LocalList): Promise<void> {
  await transaction<void>(STORE_LISTS, 'readwrite', (tx) => {
    tx.objectStore(STORE_LISTS).put(list);
  });
  publishLocalListChange(list.id);
}

export function getList(id: string): Promise<LocalList | undefined> {
  return transaction<LocalList | undefined>(STORE_LISTS, 'readonly', (tx, output) => {
    const request = tx.objectStore(STORE_LISTS).get(id);
    request.onsuccess = () => {
      output.value = (request.result as LocalList | undefined) ?? undefined;
    };
  });
}

export interface BeginListCaptureResult {
  ok: boolean;
  /** Execuções de outras listas encerradas pela instalação atômica da nova execução. */
  supersededListIds: string[];
  reason?: 'ownership_mismatch' | 'module_mismatch' | 'missing_capture_state';
}

/**
 * Instala uma execução e invalida qualquer outra execução da conta na mesma transação.
 *
 * A separação entre `getList` + `putList` permitia duas abas deixarem dois snapshots ativos
 * e, depois, cada uma projetar seu próprio listId no sync. Esta primitive fecha essa janela: ao
 * confirmar o novo run, nenhum lote do run substituído ainda encontra um `captureState`
 * compatível. Os registros já capturados permanecem intactos e entram na fila de backup por
 * `dirtyAt`.
 */
export async function beginListCapture(
  list: LocalList & { captureState: StoredCaptureEngineSnapshot },
): Promise<BeginListCaptureResult> {
  if (!list.captureState) {
    return { ok: false, supersededListIds: [], reason: 'missing_capture_state' };
  }

  const result = await transaction<BeginListCaptureResult>(
    STORE_LISTS,
    'readwrite',
    (tx, output) => {
      output.value = { ok: false, supersededListIds: [] };
      const store = tx.objectStore(STORE_LISTS);
      const accountRequest = store.index(INDEX_LISTS_BY_USER).getAll(list.userId);
      const targetRequest = store.get(list.id);
      let accountLists: LocalList[] | undefined;
      let currentTarget: LocalList | undefined;
      let targetRead = false;

      const install = () => {
        if (!accountLists || !targetRead) return;
        if (currentTarget && currentTarget.userId !== list.userId) {
          output.value.reason = 'ownership_mismatch';
          return;
        }
        if (currentTarget && currentTarget.module !== list.module) {
          output.value.reason = 'module_mismatch';
          return;
        }

        const supersededListIds: string[] = [];
        for (const current of accountLists) {
          if (!current.captureState || current.id === list.id) continue;
          const next = {
            ...current,
            searching: false,
            updatedAt: list.updatedAt,
            dirtyAt: list.updatedAt,
            backupState: 'local' as const,
          };
          delete next.captureState;
          store.put(next);
          supersededListIds.push(current.id);
        }

        // O conteúdo local é a versão mais nova. Para uma recaptura da mesma lista,
        // preservamos count/backup e atualizamos somente os dados da busca + novo snapshot.
        const target = currentTarget
          ? {
              ...currentTarget,
              keywords: list.keywords,
              sites: list.sites,
              domains: list.domains,
              countries: list.countries,
              area_code: list.area_code,
              locations: list.locations,
              distance: list.distance,
              searching: true,
              updatedAt: list.updatedAt,
              captureState: list.captureState,
            }
          : list;
        store.put(target);
        output.value = { ok: true, supersededListIds };
      };
      accountRequest.onsuccess = () => {
        accountLists = (accountRequest.result as LocalList[]) ?? [];
        install();
      };
      targetRequest.onsuccess = () => {
        currentTarget = (targetRequest.result as LocalList | undefined) ?? undefined;
        targetRead = true;
        install();
      };
    },
  );

  if (result.ok) {
    publishLocalListChange(list.id);
    for (const id of result.supersededListIds) publishLocalListChange(id);
  }
  return result;
}

/**
 * Transição de domínio do botão “Nova lista”: encerra somente runs da conta/módulo.
 * Não remove a lista nem seus registros; o próximo POST v2 decidirá o novo listId.
 */
export async function abandonListCaptures(
  userId: string,
  module: ScanType,
  abandonedAt = new Date().toISOString(),
): Promise<string[]> {
  if (!userId) return [];
  const abandoned = await transaction<string[]>(STORE_LISTS, 'readwrite', (tx, output) => {
    const ids: string[] = [];
    output.value = ids;
    const store = tx.objectStore(STORE_LISTS);
    const request = store.index(INDEX_LISTS_BY_USER).getAll(userId);
    request.onsuccess = () => {
      for (const current of (request.result as LocalList[]) ?? []) {
        if (current.module !== module || !current.captureState) continue;
        const next = {
          ...current,
          searching: false,
          updatedAt: abandonedAt,
          dirtyAt: abandonedAt,
          backupState: 'local' as const,
        };
        delete next.captureState;
        store.put(next);
        ids.push(current.id);
      }
    };
  });
  for (const id of abandoned) publishLocalListChange(id);
  return abandoned;
}

/** Lista somente os dados da conta informada, da criação mais recente para a mais antiga. */
export async function getLists(userId: string): Promise<LocalList[]> {
  const lists = await transaction<LocalList[]>(STORE_LISTS, 'readonly', (tx, output) => {
    const request = tx.objectStore(STORE_LISTS).index(INDEX_LISTS_BY_USER).getAll(userId);
    request.onsuccess = () => {
      output.value = (request.result as LocalList[]) ?? [];
    };
  });
  return lists.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

/** Escritas de conteúdo sempre recolocam a lista na fila de backup. */
export async function patchList(id: string, patch: LocalListContentPatch): Promise<void> {
  const now = new Date().toISOString();
  await transaction<void>(STORE_LISTS, 'readwrite', (tx) => {
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current) return;
      store.put({
        ...current,
        ...patch,
        id: current.id,
        updatedAt: now,
        dirtyAt: now,
        backupState: 'local',
      });
    };
  });
  publishLocalListChange(id);
}

/**
 * Incorpora os semelhantes de listas locais em uma única transação.
 *
 * Os registros já existem no store `leads`; a operação de conteúdo é somente tornar esse grupo
 * visível e recolocar a lista na fila de backup. `lookalikeAt` e `dirtyAt` precisam nascer no
 * mesmo `put`: fechar o diálogo ou outra aba observar a lista nunca pode enxergar meia
 * incorporação. A rota legada faz o gesto equivalente ao marcar `lookalikes[].marked` e
 * `lookalikeRevealed` [app.js:2875].
 */
export async function markLocalLookalikesRevealed(
  ids: readonly string[],
  userId: string,
  revealedAt = new Date().toISOString(),
): Promise<string[]> {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (!userId || uniqueIds.length === 0) return [];

  const changed = await transaction<string[]>(STORE_LISTS, 'readwrite', (tx, output) => {
    const updated: string[] = [];
    output.value = updated;
    const store = tx.objectStore(STORE_LISTS);

    for (const id of uniqueIds) {
      const request = store.get(id);
      request.onsuccess = () => {
        const current = request.result as LocalList | undefined;
        if (
          !current ||
          current.userId !== userId ||
          (current.module !== 'emails' && current.module !== 'phones') ||
          current.lookalikeAt
        ) {
          return;
        }

        store.put({
          ...current,
          lookalikeAt: revealedAt,
          updatedAt: revealedAt,
          dirtyAt: revealedAt,
          backupState: 'local',
        });
        updated.push(id);
      };
    }
  });

  for (const id of changed) publishLocalListChange(id);
  return changed;
}

export interface LookalikeResolutionResult {
  ok: boolean;
  /** Contatos efetivamente gravados (duplicatas de leads já existentes não contam). */
  inserted: number;
  /** Registros `profile` do snapshot processado que foram removidos, resolvidos ou não. */
  discarded: number;
  count: number;
  /** Só é true quando nenhum perfil posterior ao snapshot permaneceu na lista. */
  completed: boolean;
}

/**
 * Conclui a revelação de semelhantes numa transação única (D-SEM-8,
 * `docs/local-first/07-semelhantes.md` §4.3).
 *
 * O registro `profile` é **de trabalho**, não um lead: existe só enquanto o cruzamento não
 * aconteceu. Ao final somem somente os perfis do snapshot imutável confirmado pelo backend,
 * inclusive os que não resolveram. Perfis capturados depois do início permanecem ocultos e
 * deixam a lista aberta para uma nova rodada.
 *
 * Fazer as três coisas na mesma transação é o que impede a revelação de ficar pela metade: ou a
 * lista ganha os contatos, perde os perfis processados e, se não houver perfil novo, é marcada;
 * ou nada disso aconteceu.
 */
export async function applyLookalikeResolution(
  listId: string,
  userId: string,
  contacts: readonly LocalLead[],
  processedProfileKeys: readonly string[],
  snapshot: string,
  resolvedAt = new Date().toISOString(),
): Promise<LookalikeResolutionResult> {
  const empty: LookalikeResolutionResult = {
    ok: false,
    inserted: 0,
    discarded: 0,
    count: 0,
    completed: false,
  };
  if (!listId || !userId) return empty;
  if (contacts.some((record) => record.listId !== listId)) {
    throw new CaptureRecordsListMismatchError();
  }

  const result = await transaction<LookalikeResolutionResult>(
    [STORE_LISTS, STORE_LEADS],
    'readwrite',
    (tx, output) => {
      output.value = empty;
      const listStore = tx.objectStore(STORE_LISTS);
      const recordStore = tx.objectStore(STORE_LEADS);
      const listRequest = listStore.get(listId);

      listRequest.onsuccess = () => {
        const current = listRequest.result as LocalList | undefined;
        if (
          !current ||
          current.userId !== userId ||
          (current.module !== 'emails' && current.module !== 'phones') ||
          current.lookalikeAt
        ) {
          return;
        }

        let inserted = 0;
        for (const record of contacts) {
          const addRequest = recordStore.add(record);
          addRequest.onsuccess = () => {
            inserted += 1;
          };
          addRequest.onerror = (event) => {
            // O contato resolvido pode já existir na lista por captura direta. Duplicata é
            // resultado esperado, não falha da revelação.
            if (addRequest.error?.name !== 'ConstraintError') return;
            event.preventDefault();
            event.stopPropagation();
          };
        }

        const processed = new Set(processedProfileKeys);
        let discarded = 0;
        let remainingProfiles = 0;
        let visibleCount = 0;
        const cursorRequest = recordStore
          .index(INDEX_RECORDS_BY_LIST)
          .openCursor(IDBKeyRange.only(listId));
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor) {
            const record = cursor.value as LocalLead;
            if (record.type === 'profile') {
              if (processed.has(record.key)) {
                cursor.delete();
                discarded += 1;
              } else {
                // Capturado depois que o backend fixou o snapshot: fica para a próxima rodada.
                remainingProfiles += 1;
              }
            } else if (isVisibleListRecord(record, current.module)) visibleCount += 1;
            cursor.continue();
            return;
          }

          const completed = remainingProfiles === 0;
          const changed =
            inserted > 0 || discarded > 0 || current.count !== visibleCount || completed;
          if (changed) {
            listStore.put({
              ...current,
              count: visibleCount,
              ...(completed
                ? { lookalikeAt: resolvedAt, lookalikeSnapshot: snapshot }
                : {}),
              updatedAt: resolvedAt,
              dirtyAt: resolvedAt,
              backupState: 'local',
            });
          }
          output.value = {
            ok: true,
            inserted,
            discarded,
            count: visibleCount,
            completed,
          };
        };
      };
    },
  );

  if (result.ok) publishLocalListChange(listId);
  return result;
}

/** Atualiza somente a contabilidade do envio, sem alterar conteúdo nem `dirtyAt`. */
export async function patchListBackup(id: string, patch: LocalListBackupPatch): Promise<boolean> {
  const patched = await transaction<boolean>(STORE_LISTS, 'readwrite', (tx, output) => {
    output.value = false;
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current) return;
      const next = { ...current };
      for (const [key, value] of Object.entries(patch) as Array<
        [keyof LocalListBackupPatch, LocalListBackupPatch[keyof LocalListBackupPatch]]
      >) {
        if (value === undefined) delete next[key];
        else Object.assign(next, { [key]: value });
      }
      store.put({ ...next, id: current.id });
      output.value = true;
    };
  });
  if (patched && 'backupState' in patch) publishLocalListChange(id);
  return patched;
}

/**
 * Substitui a posição da mesma execução sem sujar conteúdo nem interferir no backup.
 * Um runtime antigo não pode sobrescrever outra execução nem recriar estado já finalizado.
 * A única exceção é a migração do snapshot v1, que ainda não possuía `runId`.
 */
export function setListCaptureState(
  id: string,
  captureState: CaptureEngineSnapshot,
): Promise<boolean> {
  return transaction<boolean>(STORE_LISTS, 'readwrite', (tx, output) => {
    output.value = false;
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current) return;
      const currentState = current.captureState;
      if (!currentState) return;
      if (
        captureState.listId !== id ||
        currentState.listId !== id ||
        currentState.module !== captureState.module
      ) {
        return;
      }
      if (currentState.version !== 1 && currentState.runId !== captureState.runId) return;
      const next = { ...current };
      next.captureState = captureState;
      store.put(next);
      output.value = true;
    };
  });
}

/**
 * Conclui a execução esperada atomicamente. Um callback atrasado de outro `runId` não remove
 * nem altera a captura que ocupa a mesma lista agora.
 */
export async function finishListCapture(id: string, expectedRunId: string): Promise<boolean> {
  const now = new Date().toISOString();
  const finished = await transaction<boolean>(STORE_LISTS, 'readwrite', (tx, output) => {
    output.value = false;
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current) return;
      if (current.captureState?.version !== 2 || current.captureState.runId !== expectedRunId) {
        return;
      }
      const next = {
        ...current,
        searching: false,
        updatedAt: now,
        dirtyAt: now,
        backupState: 'local' as const,
      };
      delete next.captureState;
      store.put(next);
      output.value = true;
    };
  });
  if (finished) publishLocalListChange(id);
  return finished;
}

/**
 * Marcas que esperam recibo: `/enhanced` só existe para places e `/lookalike` só para
 * emails/phones. Em places, `lookalikeAt` marca a rodada de semelhantes do Maps, cujos registros
 * vão nos chunks; exigir recibo dela deixava a lista pendente para sempre e a fila reenviava
 * `/enhanced` a cada rescan.
 */
export function enhancedAwaitsReceipt(
  list: LocalList,
  confirmed: string | undefined = list.backupEnhancedAt,
): boolean {
  return list.module === 'places' && Boolean(list.enrichedAt) && list.enrichedAt !== confirmed;
}

export function lookalikeAwaitsReceipt(
  list: LocalList,
  confirmed: string | undefined = list.backupLookalikeAt,
): boolean {
  return (
    (list.module === 'emails' || list.module === 'phones') &&
    Boolean(list.lookalikeAt) &&
    list.lookalikeAt !== confirmed
  );
}

/**
 * Confirma conteúdo e marcas apenas se nada mudou desde o início do upload. A leitura, as
 * comparações e os recibos compartilham a mesma transação para fechar a corrida.
 */
export async function markListSynced(id: string, options: MarkListSyncedOptions): Promise<boolean> {
  const synced = await transaction<boolean>(STORE_LISTS, 'readwrite', (tx, output) => {
    output.value = false;
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(id);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current) return;

      if (current.dirtyAt !== options.expectedDirtyAt) {
        // Fallback sem Web Locks: outra aba pode ter confirmado exatamente esta remessa
        // primeiro. Não rebaixe um registro que já está limpo e sincronizado.
        const markersAreSynced =
          !enhancedAwaitsReceipt(current) && !lookalikeAwaitsReceipt(current);
        if (
          current.dirtyAt === undefined &&
          current.backupState === 'synced' &&
          markersAreSynced
        ) {
          output.value = true;
          return;
        }
        store.put({ ...current, backupState: 'local' });
        return;
      }

      const backupEnhancedAt = options.confirmedMarkers?.enrichedAt ?? current.backupEnhancedAt;
      const backupLookalikeAt =
        options.confirmedMarkers?.lookalikeAt ?? current.backupLookalikeAt;
      if (
        enhancedAwaitsReceipt(current, backupEnhancedAt) ||
        lookalikeAwaitsReceipt(current, backupLookalikeAt)
      ) {
        // Um marcador novo apareceu sem confirmação correspondente. Ele continua sendo trabalho
        // da própria lista; nunca limpe a fila só porque os chunks anteriores terminaram.
        store.put({ ...current, backupState: 'local' });
        return;
      }

      const clean = { ...current };
      delete clean.dirtyAt;
      delete clean.backupError;
      delete clean.backupAttempts;
      delete clean.backupNextAttemptAt;
      delete clean.backupBatchId;
      delete clean.backupChunkSize;
      delete clean.backupConfirmedChunks;
      store.put({
        ...clean,
        backupState: 'synced',
        backupAt: options.backupAt,
        ...(backupEnhancedAt ? { backupEnhancedAt } : {}),
        ...(backupLookalikeAt ? { backupLookalikeAt } : {}),
      });
      output.value = true;
    };
  });
  publishLocalListChange(id);
  return synced;
}

/** Apaga lista, leads e places atomicamente; falha em qualquer store desfaz a cascata inteira. */
export async function deleteList(id: string): Promise<void> {
  await transaction<void>([STORE_LISTS, STORE_LEADS, STORE_PLACES], 'readwrite', (tx) => {
    tx.objectStore(STORE_LISTS).delete(id);

    for (const storeName of [STORE_LEADS, STORE_PLACES] as const) {
      const request = tx
        .objectStore(storeName)
        .index(INDEX_RECORDS_BY_LIST)
        .openCursor(IDBKeyRange.only(id));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
      };
    }
  });
  publishLocalListChange(id);
}

function storeFor(module: ScanType): RecordStore {
  return module === 'places' ? STORE_PLACES : STORE_LEADS;
}

/** Conta somente registros exibíveis dentro da transação que acabou de alterá-los. */
function countVisibleRecordsInStore(
  store: IDBObjectStore,
  listId: string,
  module: ScanType,
  onComplete: (count: number) => void,
): void {
  let count = 0;
  const request = store
    .index(INDEX_RECORDS_BY_LIST)
    .openCursor(IDBKeyRange.only(listId));
  request.onsuccess = () => {
    const cursor = request.result;
    if (!cursor) {
      onComplete(count);
      return;
    }
    if (isVisibleListRecord(cursor.value as LocalRecord, module)) count += 1;
    cursor.continue();
  };
}

/** Conta registros pela chave `listId`; com filtro, usa uma única varredura por cursor. */
export function countRecords(
  module: ScanType,
  listId: string,
  filter?: (record: LocalRecord) => boolean,
): Promise<number> {
  const storeName = storeFor(module);
  return transaction<number>(storeName, 'readonly', (tx, output) => {
    const index = tx.objectStore(storeName).index(INDEX_RECORDS_BY_LIST);
    if (!filter) {
      const request = index.count(IDBKeyRange.only(listId));
      request.onsuccess = () => {
        output.value = request.result;
      };
      return;
    }

    let total = 0;
    output.value = 0;
    const request = index.openCursor(IDBKeyRange.only(listId));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (filter(cursor.value as LocalRecord)) total += 1;
      output.value = total;
      cursor.continue();
    };
  });
}

/**
 * Retorna página e total numa única varredura. O filtro é aplicado antes de offset/limit, e
 * `limit: 0` significa todos os registros restantes.
 */
export function getPage<T extends LocalRecord = LocalRecord>(
  module: ScanType,
  listId: string,
  options: {
    limit?: number;
    offset?: number;
    filter?: (record: LocalRecord) => boolean;
    /**
     * Devolve as primeiras chaves pela âncora imutável `(createdAt ASC, key ASC)`. A seleção
     * acontece antes do filtro e da janela, na mesma snapshot da página, e pula os registros
     * fechados ou já embaralhados.
     */
    anchorLimit?: number;
  } = {},
): Promise<{ records: T[]; total: number; anchorKeys: string[] }> {
  const storeName = storeFor(module);
  const limit = Math.max(0, Math.trunc(options.limit ?? 0));
  const offset = Math.max(0, Math.trunc(options.offset ?? 0));
  const anchorLimit = Math.max(0, Math.trunc(options.anchorLimit ?? 0));

  return transaction<{ records: T[]; total: number; anchorKeys: string[] }>(
    storeName,
    'readonly',
    (tx, output) => {
      const records: T[] = [];
      const anchors: Array<Pick<LocalRecord, 'createdAt' | 'key'>> = [];
      let total = 0;
      output.value = { records, total, anchorKeys: [] };

      const request = tx
        .objectStore(storeName)
        .index(INDEX_RECORDS_BY_LIST)
        .openCursor(IDBKeyRange.only(listId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          output.value = {
            records,
            total,
            anchorKeys: anchors.map((anchor) => anchor.key),
          };
          return;
        }

        const record = cursor.value as LocalRecord;
        // Registro fechado ou já embaralhado nunca ocupa uma vaga de leitura (decisão 293).
        if (anchorLimit > 0 && record.deface !== true) {
          anchors.push({ createdAt: record.createdAt, key: record.key });
          anchors.sort(
            (left, right) =>
              left.createdAt.localeCompare(right.createdAt) || left.key.localeCompare(right.key),
          );
          if (anchors.length > anchorLimit) anchors.pop();
        }
        if (!options.filter || options.filter(record)) {
          if (total >= offset && (limit === 0 || records.length < limit)) {
            records.push(record as T);
          }
          total += 1;
        }
        cursor.continue();
      };
    },
  );
}

/**
 * Atualiza places conhecidos e recoloca suas listas na fila de backup na MESMA transação.
 *
 * O chamador informa chaves obtidas de uma leitura anterior, mas cada registro é relido dentro
 * da transação antes do merge. Assim, uma captura concorrente não é sobrescrita por uma cópia
 * velha mantida em memória durante um enriquecimento demorado.
 */
export async function updateLocalPlacesByTarget(
  userId: string,
  targets: readonly LocalPlaceTarget[],
  update: (current: LocalPlace) => LocalPlace,
  updatedAt = new Date().toISOString(),
): Promise<LocalPlaceUpdateResult[]> {
  if (!userId || targets.length === 0) return [];

  const uniqueTargets = new Map<string, LocalPlaceTarget>();
  for (const target of targets) {
    uniqueTargets.set(`${target.listId}\u0000${target.key}`, target);
  }

  const result = await transaction<{
    records: LocalPlaceUpdateResult[];
    listIds: string[];
  }>([STORE_LISTS, STORE_PLACES], 'readwrite', (tx, output) => {
    const records: LocalPlaceUpdateResult[] = [];
    const touched = new Set<string>();
    output.value = { records, listIds: [] };
    const listStore = tx.objectStore(STORE_LISTS);
    const placeStore = tx.objectStore(STORE_PLACES);
    const listsRequest = listStore.index(INDEX_LISTS_BY_USER).getAll(userId);

    listsRequest.onsuccess = () => {
      const owned = new Set(
        ((listsRequest.result as LocalList[]) ?? [])
          .filter((list) => list.module === 'places')
          .map((list) => list.id),
      );

      for (const target of uniqueTargets.values()) {
        if (!owned.has(target.listId)) continue;
        const request = placeStore.get([target.listId, target.key]);
        request.onsuccess = () => {
          const current = request.result as LocalPlace | undefined;
          if (!current || current.cid !== target.cid) return;

          const candidate = update(current);
          const after: LocalPlace = {
            ...candidate,
            listId: current.listId,
            key: current.key,
            cid: current.cid,
          };
          placeStore.put(after);
          records.push({ before: current, after });

          if (touched.has(current.listId)) return;
          touched.add(current.listId);
          output.value.listIds = [...touched];
          const listRequest = listStore.get(current.listId);
          listRequest.onsuccess = () => {
            const list = listRequest.result as LocalList | undefined;
            if (!list || list.userId !== userId || list.module !== 'places') return;
            listStore.put({
              ...list,
              updatedAt,
              dirtyAt: updatedAt,
              backupState: 'local',
            });
          };
        };
      }
    };
  });

  for (const listId of result.listIds) publishLocalListChange(listId);
  return result.records;
}

/**
 * Passa `repair` em todos os places das listas da pessoa e grava só os que mudaram, recolocando
 * cada lista alterada na fila de backup na mesma transação (a regra de
 * `updateLocalPlacesByTarget`). Serve para corrigir registros já gravados.
 */
export async function repairLocalPlaces(
  userId: string,
  repair: (current: LocalPlace) => LocalPlace | undefined,
  updatedAt = new Date().toISOString(),
): Promise<{ repaired: number; listIds: string[] }> {
  if (!userId) return { repaired: 0, listIds: [] };

  const result = await transaction<{ repaired: number; listIds: string[] }>(
    [STORE_LISTS, STORE_PLACES],
    'readwrite',
    (tx, output) => {
      output.value = { repaired: 0, listIds: [] };
      const listStore = tx.objectStore(STORE_LISTS);
      const placesByList = tx.objectStore(STORE_PLACES).index(INDEX_RECORDS_BY_LIST);
      const listsRequest = listStore.index(INDEX_LISTS_BY_USER).getAll(userId);

      listsRequest.onsuccess = () => {
        for (const list of (listsRequest.result as LocalList[]) ?? []) {
          if (list.module !== 'places') continue;
          let touched = false;
          const cursorRequest = placesByList.openCursor(IDBKeyRange.only(list.id));
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) {
              if (!touched) return;
              output.value.listIds.push(list.id);
              listStore.put({ ...list, updatedAt, dirtyAt: updatedAt, backupState: 'local' });
              return;
            }
            const current = cursor.value as LocalPlace;
            const candidate = repair(current);
            if (candidate) {
              cursor.update({
                ...candidate,
                listId: current.listId,
                key: current.key,
                cid: current.cid,
              });
              output.value.repaired += 1;
              touched = true;
            }
            cursor.continue();
          };
        }
      };
    },
  );

  for (const listId of result.listIds) publishLocalListChange(listId);
  return result;
}

/**
 * Insere sem sobrescrever e devolve quantos registros eram novos. `ConstraintError` representa
 * deduplicação por `[listId, key]`; outros erros, inclusive cota, abortam e chegam ao chamador.
 */
export function insertIfAbsent(
  store: typeof STORE_LEADS,
  records: readonly LocalLead[],
): Promise<number>;
export function insertIfAbsent(
  store: typeof STORE_PLACES,
  records: readonly LocalPlace[],
): Promise<number>;
export async function insertIfAbsent(
  store: RecordStore,
  records: readonly LocalRecord[],
): Promise<number> {
  if (records.length === 0) return 0;

  return transaction<number>(store, 'readwrite', (tx, output) => {
    const objectStore = tx.objectStore(store);
    let inserted = 0;
    output.value = 0;

    for (const record of records) {
      const request = objectStore.add(record);
      request.onsuccess = () => {
        inserted += 1;
        output.value = inserted;
      };
      request.onerror = (event) => {
        if (request.error?.name !== 'ConstraintError') return;
        event.preventDefault();
        event.stopPropagation();
      };
    }
  });
}

/**
 * Torna uma hidratação concluída visível sem transformá-la em conteúdo pendente de upload.
 * A recontagem e o merge da lista compartilham a transação: uma lista local existente preserva
 * integralmente `dirtyAt` e a contabilidade de backup; uma lista que só existia no servidor
 * nasce limpa, pois todos os registros acabaram de vir do próprio backup.
 */
export async function completeListHydration(
  options: CompleteListHydrationOptions,
): Promise<ListHydrationWriteResult> {
  const recordStoreName = storeFor(options.module);
  const result = await transaction<ListHydrationWriteResult>(
    [STORE_LISTS, recordStoreName],
    'readwrite',
    (tx, output) => {
      output.value = { ok: false, reason: 'module_mismatch' };
      const listStore = tx.objectStore(STORE_LISTS);
      const recordStore = tx.objectStore(recordStoreName);
      const listRequest = listStore.get(options.id);
      let listLoaded = false;
      let countLoaded = false;
      let current: LocalList | undefined;
      let count = 0;

      const finish = () => {
        if (!listLoaded || !countLoaded) return;
        if (current && current.userId !== options.userId) {
          output.value = { ok: false, reason: 'ownership_mismatch' };
          return;
        }
        if (current && current.module !== options.module) {
          output.value = { ok: false, reason: 'module_mismatch' };
          return;
        }

        const sealedAt =
          options.sealed && !current?.sealedAt ? { sealedAt: options.hydratedAt } : {};
        if (current) {
          if (current.count !== count || 'sealedAt' in sealedAt) {
            listStore.put({ ...current, count, ...sealedAt });
          }
        } else {
          listStore.put({
            id: options.id,
            userId: options.userId,
            module: options.module,
            count,
            searching: false,
            createdAt: options.hydratedAt,
            updatedAt: options.hydratedAt,
            backupState: 'synced',
            ...sealedAt,
          } satisfies LocalList);
        }
        output.value = { ok: true, count };
      };

      listRequest.onsuccess = () => {
        current = (listRequest.result as LocalList | undefined) ?? undefined;
        listLoaded = true;
        finish();
      };
      countVisibleRecordsInStore(recordStore, options.id, options.module, (visibleCount) => {
        count = visibleCount;
        countLoaded = true;
        finish();
      });
    },
  );
  if (result.ok) publishLocalListChange(options.id);
  return result;
}

export function insertCaptureRecords(
  module: Exclude<ScanType, 'places'>,
  listId: string,
  records: readonly LocalLead[],
  options?: InsertCaptureRecordsOptions,
): Promise<CaptureRecordsWriteResult>;
export function insertCaptureRecords(
  module: 'places',
  listId: string,
  records: readonly LocalPlace[],
  options?: InsertCaptureRecordsOptions,
): Promise<CaptureRecordsWriteResult>;
/**
 * Confirma um lote numa transação única: valida a lista, insere sem sobrescrever, reconta e
 * marca conteúdo novo como pendente de backup. Retry composto só de duplicatas não ressujará a
 * lista quando a contagem já estiver correta.
 */
export function insertCaptureRecords(
  module: ScanType,
  listId: string,
  records: readonly LocalRecord[],
  options: InsertCaptureRecordsOptions = {},
): Promise<CaptureRecordsWriteResult> {
  if (records.some((record) => record.listId !== listId)) {
    throw new CaptureRecordsListMismatchError();
  }

  const recordStoreName = storeFor(module);
  const now = new Date().toISOString();
  const operation = transaction<CaptureRecordsWriteResult>(
    [STORE_LISTS, recordStoreName],
    'readwrite',
    (tx, output) => {
      const listStore = tx.objectStore(STORE_LISTS);
      const recordStore = tx.objectStore(recordStoreName);
      const listRequest = listStore.get(listId);

      listRequest.onsuccess = () => {
        const current = listRequest.result as LocalList | undefined;
        if (!current) {
          output.value = { ok: false, reason: 'list_not_found' };
          return;
        }
        if (current.module !== module) {
          output.value = { ok: false, reason: 'module_mismatch' };
          return;
        }
        if (
          options.expectedRunId &&
          (current.captureState?.version !== 2 ||
            current.captureState.runId !== options.expectedRunId)
        ) {
          // A checagem compartilha a transação dos inserts. Uma execução substituída entre a
          // normalização e esta escrita não consegue vazar registros para o run novo.
          output.value = { ok: false, reason: 'stale_run', count: current.count };
          return;
        }

        let inserted = 0;
        let sealedInserted = false;
        const insertedKeys: string[] = [];
        for (const record of records) {
          const addRequest = recordStore.add(record);
          addRequest.onsuccess = () => {
            inserted += 1;
            insertedKeys.push(record.key);
            if (isSealedKey(record.key)) sealedInserted = true;
          };
          addRequest.onerror = (event) => {
            if (addRequest.error?.name !== 'ConstraintError') return;
            event.preventDefault();
            event.stopPropagation();
          };
        }

        countVisibleRecordsInStore(recordStore, listId, module, (count) => {
          if (inserted > 0 || current.count !== count) {
            const next = { ...current };
            if (
              inserted > 0 &&
              records.some((record) => 'type' in record && record.type === 'profile')
            ) {
              // Uma captura posterior reabre semelhantes. O recibo remoto anterior permanece em
              // `backupLookalikeAt`; a nova rodada ganhará outra marca depois de resolver.
              delete next.lookalikeAt;
              delete next.lookalikeSnapshot;
            }
            listStore.put({
              ...next,
              count,
              updatedAt: now,
              dirtyAt: now,
              backupState: 'local',
              ...(sealedInserted && !next.sealedAt ? { sealedAt: now } : {}),
            });
          }
          output.value = { ok: true, inserted, count, insertedKeys };
        });
      };
    },
  );
  return operation.then((result) => {
    if (result.ok) publishLocalListChange(listId);
    return result;
  });
}

/** Primitivas do cache de MX; TTL e resolução entram em S2.4. */
export async function putDomainVerdict(verdict: DomainVerdict): Promise<void> {
  await transaction<void>(STORE_DOMAINS, 'readwrite', (tx) => {
    tx.objectStore(STORE_DOMAINS).put(verdict);
  });
}

export function getDomainVerdict(domain: string): Promise<DomainVerdict | undefined> {
  return transaction<DomainVerdict | undefined>(STORE_DOMAINS, 'readonly', (tx, output) => {
    const request = tx.objectStore(STORE_DOMAINS).get(domain);
    request.onsuccess = () => {
      output.value = (request.result as DomainVerdict | undefined) ?? undefined;
    };
  });
}

export async function deleteDomainVerdict(domain: string): Promise<void> {
  await transaction<void>(STORE_DOMAINS, 'readwrite', (tx) => {
    tx.objectStore(STORE_DOMAINS).delete(domain);
  });
}

/** Fecha e apaga o banco para isolar testes. */
export async function __resetLocalDb(): Promise<void> {
  const current = dbPromise;
  dbPromise = null;
  capacityPromise = null;

  if (current) {
    try {
      (await current).close();
    } catch {
      // Uma abertura que já falhou não possui conexão para fechar.
    }
  }

  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(LOCAL_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('Falha ao apagar o banco local.'));
    request.onblocked = () => resolve();
  });
}

/* --------------------------- Registros fechados --------------------------- */

const SEALED_KEY_PREFIX = 'masked:';

/** Chave de registro fechado no navegador ou mascarado pelo servidor (decisão 293). */
export function isSealedKey(key: string): boolean {
  return key.startsWith(SEALED_KEY_PREFIX);
}

/** As chaves do lote que já estão gravadas na lista (decisão 293: o fechado não duplica o aberto). */
export async function existingRecordKeys(
  module: ScanType,
  listId: string,
  keys: readonly string[],
): Promise<Set<string>> {
  const found = new Set<string>();
  if (keys.length === 0) return found;
  await transaction<void>(storeFor(module), 'readonly', (tx) => {
    const store = tx.objectStore(storeFor(module));
    for (const key of new Set(keys)) {
      const request = store.getKey([listId, key]);
      request.onsuccess = () => {
        if (request.result !== undefined) found.add(key);
      };
    }
  });
  return found;
}

export type RemoveSealedRecordsResult =
  | { ok: true; removed: number }
  | { ok: false; reason: 'not_synced' | 'list_not_found' };

/**
 * Tira da lista os registros fechados ou mascarados (chave `masked:`), para ler a lista de novo
 * do servidor depois da compra. Só com a cópia na nuvem em dia, conferida na mesma transação:
 * antes disso, o original ainda não chegou ao servidor e se perderia. A marca `sealedAt` fica até
 * a leitura terminar (`clearListSealed`), para uma leitura interrompida ser retomada.
 */
export async function removeSealedRecords(listId: string): Promise<RemoveSealedRecordsResult> {
  const list = await getList(listId);
  if (!list) return { ok: false, reason: 'list_not_found' };
  const storeName = storeFor(list.module);
  return transaction<RemoveSealedRecordsResult>(
    [STORE_LISTS, storeName],
    'readwrite',
    (tx, output) => {
      output.value = { ok: false, reason: 'list_not_found' };
      const listRequest = tx.objectStore(STORE_LISTS).get(listId);
      listRequest.onsuccess = () => {
        const current = listRequest.result as LocalList | undefined;
        if (!current) return;
        if (current.backupState !== 'synced') {
          output.value = { ok: false, reason: 'not_synced' };
          return;
        }
        const store = tx.objectStore(storeName);
        const range = IDBKeyRange.bound(
          [listId, SEALED_KEY_PREFIX],
          [listId, `${SEALED_KEY_PREFIX}\uffff`],
        );
        const countRequest = store.count(range);
        countRequest.onsuccess = () => {
          output.value = { ok: true, removed: countRequest.result };
          if (countRequest.result > 0) store.delete(range);
        };
      };
    },
  );
}

/** A lista foi lida de novo do servidor: tira a marca de registros fechados. */
export async function clearListSealed(listId: string): Promise<void> {
  await transaction<void>(STORE_LISTS, 'readwrite', (tx) => {
    const store = tx.objectStore(STORE_LISTS);
    const request = store.get(listId);
    request.onsuccess = () => {
      const current = request.result as LocalList | undefined;
      if (!current?.sealedAt) return;
      const next = { ...current };
      delete next.sealedAt;
      store.put(next);
    };
  });
  publishLocalListChange(listId);
}
